import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Snackbar,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    Search as SearchIcon,
    Clear as ClearIcon,
    Refresh as RefreshIcon,
    AutoAwesome as AutoAwesomeIcon,
    Publish as PublishIcon,
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorOutlineIcon,
    Folder as FolderIcon,
    Image as ImageIcon,
} from "@mui/icons-material";

import apiClient from "../../Helpers/apiClient";

import BackToCPQButton from "../../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../../Components/PreviousPageButtonCPQ";

function AscoraProjectsPublisher() {
    // ============================================================
    // TABLE STATE
    // ============================================================

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [filterText, setFilterText] = useState("");

    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("completedDate");

    // ============================================================
    // MODAL STATE
    // ============================================================

    const [openDialog, setOpenDialog] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [eligibilityReason, setEligibilityReason] = useState("");
    const [description, setDescription] = useState("");

    const [generating, setGenerating] = useState(false);
    const [publishing, setPublishing] = useState(false);

    // ============================================================
    // MANUAL JOB SEARCH
    // ============================================================

    const [manualJobNumber, setManualJobNumber] = useState("");
    const [checkingJob, setCheckingJob] = useState(false);

    // ============================================================
    // BULK OPERATIONS
    // ============================================================

    const [bulkGenerating, setBulkGenerating] = useState(false);
    const [bulkPublishing, setBulkPublishing] = useState(false);

    // ============================================================
    // AUTO MODE
    // ============================================================

    const [autoMode, setAutoMode] = useState(null);
    const [autoModeLoading, setAutoModeLoading] = useState(true);
    const [autoModeSaving, setAutoModeSaving] = useState(false);
    const [autoModeRunning, setAutoModeRunning] = useState(false);

    // Stores generated / edited descriptions by job number.
    const [projectDescriptions, setProjectDescriptions] = useState({});

    // ============================================================
    // NOTIFICATION
    // ============================================================

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // ============================================================
    // GLOBAL UI LOCK
    // ============================================================

    const isBusy =
        loading ||
        generating ||
        publishing ||
        checkingJob ||
        bulkGenerating ||
        bulkPublishing ||
        autoModeRunning;

    // ============================================================
    // LOAD ELIGIBLE PROJECTS
    // ============================================================

    useEffect(() => {
        fetchEligibleProjects();
        fetchAutoMode();
    }, []);

    const fetchAutoMode = async () => {
        setAutoModeLoading(true);
        try {
            const response = await apiClient.get("/api/projects/auto-mode");
            setAutoMode(response.data || null);
        } catch (err) {
            console.error("Failed to load Auto Mode status:", err);
            showSnackbar(
                err.response?.data?.message || err.message || "Failed to load Auto Mode status.",
                "error",
            );
        } finally {
            setAutoModeLoading(false);
        }
    };

    const handleAutoModeToggle = async (event) => {
        const enabled = event.target.checked;
        const previous = autoMode;
        setAutoMode((current) => ({ ...(current || {}), enabled }));
        setAutoModeSaving(true);

        try {
            const response = await apiClient.post("/api/projects/auto-mode", { enabled });
            setAutoMode(response.data);
            showSnackbar(`Auto Publish Mode ${enabled ? "enabled" : "disabled"}.`, "success");
        } catch (err) {
            setAutoMode(previous);
            showSnackbar(
                err.response?.data?.message || err.message || "Failed to update Auto Mode.",
                "error",
            );
        } finally {
            setAutoModeSaving(false);
        }
    };

    const handleRunAutoModeNow = async () => {
        if (
            !window.confirm(
                "Run the unattended publishing cycle now? Eligible projects will be published live.",
            )
        )
            return;

        setAutoModeRunning(true);
        try {
            const response = await apiClient.post("/api/projects/auto-mode/run-now");
            const result = response.data || {};
            showSnackbar(
                `Auto run complete: ${result.publishedCount || 0} published, ${result.skippedCount || 0} skipped, ${result.failedCount || 0} failed.`,
                result.failedCount > 0 ? "warning" : "success",
            );
            await Promise.all([fetchAutoMode(), fetchEligibleProjects()]);
        } catch (err) {
            showSnackbar(err.response?.data?.message || err.message || "Auto publishing failed.", "error");
            await fetchAutoMode();
        } finally {
            setAutoModeRunning(false);
        }
    };

    const formatAutoDate = (value) => {
        if (!value) return "Never";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? "Never" : date.toLocaleString();
    };

    const fetchEligibleProjects = async () => {
        setLoading(true);

        try {
            const response = await apiClient.get("/api/projects/eligible");

            const data = Array.isArray(response.data) ? response.data : [];

            setProjects(data);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch eligible projects:", err);

            setProjects([]);

            setError(
                err.response?.data?.message || err.message || "Failed to load eligible Ascora projects.",
            );
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // TABLE FILTERING
    // ============================================================

    const filteredProjects = useMemo(() => {
        if (!filterText.trim()) {
            return projects;
        }

        const search = filterText.trim().toLowerCase();

        return projects.filter((project) => {
            const values = [
                project.jobNumber,
                project.jobName,
                project.product,
                project.workUndertaken,
                project.suburb,
                project.oneDriveFolderName,
            ];

            return values.some((value) =>
                String(value || "")
                    .toLowerCase()
                    .includes(search),
            );
        });
    }, [projects, filterText]);

    // ============================================================
    // SORTING
    // ============================================================

    const handleRequestSort = (property) => {
        if (isBusy) {
            return;
        }

        const isAsc = orderBy === property && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const displayProjects = useMemo(() => {
        return [...filteredProjects].sort((a, b) => {
            let valueA = a[orderBy];
            let valueB = b[orderBy];

            if (orderBy === "completedDate") {
                valueA = valueA ? new Date(valueA).getTime() : 0;
                valueB = valueB ? new Date(valueB).getTime() : 0;
            } else {
                valueA = String(valueA || "").toLowerCase();
                valueB = String(valueB || "").toLowerCase();
            }

            if (valueA < valueB) {
                return order === "asc" ? -1 : 1;
            }

            if (valueA > valueB) {
                return order === "asc" ? 1 : -1;
            }

            return 0;
        });
    }, [filteredProjects, order, orderBy]);

    // ============================================================
    // OPEN PROJECT
    // ============================================================

    const handleOpenProject = (project) => {
        if (isBusy) {
            return;
        }

        setSelectedProject(project);
        setEligibilityReason("");

        setDescription(projectDescriptions[project.jobNumber] || project.generatedDescription || "");

        setOpenDialog(true);
    };

    // ============================================================
    // AI GENERATION PAYLOAD
    // Uses the already-loaded eligible project data so the backend
    // can call OpenAI directly without re-running eligibility.
    // ============================================================

    const buildGenerationPayload = (project) => ({
        jobId: project.jobId,
        jobNumber: project.jobNumber,
        jobName: project.jobName,
        product: project.product,
        workUndertaken: project.workUndertaken,
        completedDate: project.completedDate,
        suburb: project.suburb,
        oneDriveFolderId: project.oneDriveFolderId,
        oneDriveFolderName: project.oneDriveFolderName,
        oneDriveLastModifiedDateTime: project.oneDriveLastModifiedDateTime,
    });

    // ============================================================
    // BULK GENERATE
    // ============================================================

    const handleBulkGenerate = async () => {
        if (isBusy) {
            return;
        }

        if (projects.length === 0) {
            showSnackbar("There are no eligible projects to generate.", "warning");

            return;
        }

        setBulkGenerating(true);

        let successCount = 0;
        let failureCount = 0;

        try {
            const concurrency = 3;

            for (let i = 0; i < projects.length; i += concurrency) {
                const batch = projects.slice(i, i + concurrency);

                const results = await Promise.allSettled(
                    batch.map(async (project) => {
                        if (projectDescriptions[project.jobNumber]?.trim()) {
                            return {
                                jobNumber: project.jobNumber,
                                skipped: true,
                            };
                        }

                        const response = await apiClient.post(
                            `/api/projects/${encodeURIComponent(project.jobNumber)}/generate`,
                            buildGenerationPayload(project),
                            {
                                headers: {
                                    "Content-Type": "application/json",
                                },
                            },
                        );

                        return {
                            jobNumber: project.jobNumber,
                            description: response.data?.description || "",
                        };
                    }),
                );

                const newDescriptions = {};

                results.forEach((result, index) => {
                    const project = batch[index];

                    if (result.status === "fulfilled") {
                        if (result.value.skipped) {
                            return;
                        }

                        const generated = result.value.description;

                        if (generated?.trim()) {
                            newDescriptions[project.jobNumber] = generated;

                            successCount++;
                        } else {
                            failureCount++;
                        }
                    } else {
                        console.error(`Bulk generate failed for ${project.jobNumber}:`, result.reason);

                        failureCount++;
                    }
                });

                setProjectDescriptions((current) => ({
                    ...current,
                    ...newDescriptions,
                }));
            }

            if (failureCount === 0) {
                showSnackbar(`Generated ${successCount} project descriptions successfully.`, "success");
            } else {
                showSnackbar(`Generated ${successCount} descriptions. ${failureCount} failed.`, "warning");
            }
        } catch (err) {
            console.error("Bulk generation failed:", err);

            showSnackbar(err.response?.data?.message || err.message || "Bulk AI generation failed.", "error");
        } finally {
            setBulkGenerating(false);
        }
    };

    // ============================================================
    // CLOSE DIALOG
    // ============================================================

    const handleCloseDialog = () => {
        if (isBusy) {
            return;
        }

        setOpenDialog(false);
        setSelectedProject(null);
        setEligibilityReason("");
        setDescription("");
    };

    // ============================================================
    // BULK PUBLISH
    // ============================================================

    const handleBulkPublish = async () => {
        if (isBusy) {
            return;
        }

        const publishableProjects = projects.filter((project) =>
            projectDescriptions[project.jobNumber]?.trim(),
        );

        if (publishableProjects.length === 0) {
            showSnackbar("Generate descriptions before bulk publishing.", "warning");

            return;
        }

        const confirmed = window.confirm(
            `Publish ${publishableProjects.length} eligible projects to Shopify?\n\n` +
                "This will create live Shopify project pages.",
        );

        if (!confirmed) {
            return;
        }

        setBulkPublishing(true);

        let successCount = 0;
        let failureCount = 0;

        const failedJobs = [];

        try {
            const concurrency = 2;
            const successfulJobs = [];

            for (let i = 0; i < publishableProjects.length; i += concurrency) {
                const batch = publishableProjects.slice(i, i + concurrency);

                const results = await Promise.allSettled(
                    batch.map(async (project) => {
                        const projectDescription = projectDescriptions[project.jobNumber];

                        const response = await apiClient.post(
                            `/api/projects/${encodeURIComponent(project.jobNumber)}/publish`,
                            {
                                description: projectDescription.trim(),
                            },
                            {
                                headers: {
                                    "Content-Type": "application/json",
                                },
                            },
                        );

                        if (response.data?.success === false) {
                            throw new Error(response.data?.message || "Publishing failed.");
                        }

                        return project.jobNumber;
                    }),
                );

                results.forEach((result, index) => {
                    const project = batch[index];

                    if (result.status === "fulfilled") {
                        successCount++;
                        successfulJobs.push(project.jobNumber);
                    } else {
                        console.error(`Bulk publish failed for ${project.jobNumber}:`, result.reason);
                        failureCount++;
                        failedJobs.push(project.jobNumber);
                    }
                });
            }

            if (successfulJobs.length > 0) {
                const successfulSet = new Set(successfulJobs);

                setProjects((current) => current.filter((project) => !successfulSet.has(project.jobNumber)));

                setProjectDescriptions((current) => {
                    const copy = { ...current };

                    successfulJobs.forEach((jobNumber) => {
                        delete copy[jobNumber];
                    });

                    return copy;
                });
            }

            if (failureCount === 0) {
                showSnackbar(`Successfully published ${successCount} projects to Shopify.`, "success");
            } else {
                showSnackbar(
                    `Published ${successCount}. ${failureCount} failed: ${failedJobs.join(", ")}`,
                    "warning",
                );
            }
        } catch (err) {
            console.error("Bulk publishing failed:", err);

            showSnackbar(err.response?.data?.message || err.message || "Bulk publishing failed.", "error");
        } finally {
            setBulkPublishing(false);
        }
    };

    // ============================================================
    // MANUAL JOB CHECK
    // ============================================================

    const handleManualCheck = async () => {
        if (isBusy) {
            return;
        }

        const jobNumber = manualJobNumber.trim();

        if (!jobNumber) {
            showSnackbar("Enter a job number first.", "warning");

            return;
        }

        setCheckingJob(true);

        try {
            const response = await apiClient.get(`/api/projects/check/${encodeURIComponent(jobNumber)}`);

            const result = response.data;

            if (result?.eligible && result?.project) {
                setSelectedProject(result.project);

                setEligibilityReason("");

                setDescription(
                    projectDescriptions[result.project.jobNumber] ||
                        result.project.generatedDescription ||
                        "",
                );
            } else {
                setSelectedProject(null);

                setDescription("");

                setEligibilityReason(result?.reason || "This job is not eligible for publishing.");
            }

            setOpenDialog(true);
        } catch (err) {
            console.error("Manual eligibility check failed:", err);

            setSelectedProject(null);
            setDescription("");

            setEligibilityReason(
                err.response?.data?.reason ||
                    err.response?.data?.message ||
                    "Could not check this Ascora job.",
            );

            setOpenDialog(true);
        } finally {
            setCheckingJob(false);
        }
    };

    // ============================================================
    // GENERATE / REGENERATE
    // ============================================================

    const handleGenerateDescription = async () => {
        if (isBusy || !selectedProject?.jobNumber) {
            return;
        }

        const wasRegeneration = Boolean(description.trim());

        setGenerating(true);

        try {
            const response = await apiClient.post(
                `/api/projects/${encodeURIComponent(selectedProject.jobNumber)}/generate`,
                buildGenerationPayload(selectedProject),
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            );

            const generated = response.data?.description || "";

            setDescription(generated);

            setProjectDescriptions((current) => ({
                ...current,
                [selectedProject.jobNumber]: generated,
            }));

            showSnackbar(
                wasRegeneration
                    ? "Description regenerated successfully."
                    : "Description generated successfully.",
                "success",
            );
        } catch (err) {
            console.error("Description generation failed:", err);

            showSnackbar(
                err.response?.data?.message ||
                    err.response?.data ||
                    "Failed to generate project description.",
                "error",
            );
        } finally {
            setGenerating(false);
        }
    };

    // ============================================================
    // PUBLISH VALIDATION
    // ============================================================

    const canPublish = Boolean(
        selectedProject &&
        selectedProject.jobNumber?.trim() &&
        selectedProject.jobName?.trim() &&
        selectedProject.workUndertaken?.trim() &&
        selectedProject.product?.trim() &&
        selectedProject.completedDate &&
        selectedProject.suburb?.trim() &&
        selectedProject.oneDriveFolderId?.trim() &&
        description.trim(),
    );

    // ============================================================
    // PUBLISH
    // ============================================================

    const handlePublish = async () => {
        if (isBusy || !selectedProject || !canPublish) {
            return;
        }

        setPublishing(true);

        try {
            const response = await apiClient.post(
                `/api/projects/${encodeURIComponent(selectedProject.jobNumber)}/publish`,
                {
                    description: description.trim(),
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            );

            if (response.data?.success === false) {
                throw new Error(response.data?.message || "Publishing failed.");
            }

            showSnackbar(`${selectedProject.jobNumber} published to Shopify successfully.`, "success");

            setOpenDialog(false);

            setProjectDescriptions((current) => {
                const copy = {
                    ...current,
                };

                delete copy[selectedProject.jobNumber];

                return copy;
            });

            const publishedJobNumber = selectedProject.jobNumber;

            setSelectedProject(null);
            setDescription("");
            setEligibilityReason("");

            // Remove the published row locally instead of forcing a
            // full expensive eligibility reload after every publish.
            setProjects((current) => current.filter((project) => project.jobNumber !== publishedJobNumber));
        } catch (err) {
            console.error("Publishing failed:", err);

            showSnackbar(
                err.response?.data?.message ||
                    err.response?.data?.Message ||
                    err.message ||
                    "Failed to publish project.",
                "error",
            );
        } finally {
            setPublishing(false);
        }
    };

    // ============================================================
    // HELPERS
    // ============================================================

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((current) => ({
            ...current,
            open: false,
        }));
    };

    const formatDate = (value) => {
        if (!value) {
            return "";
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const publishableCount = projects.filter((project) =>
        projectDescriptions[project.jobNumber]?.trim(),
    ).length;

    // ============================================================
    // BUSY MESSAGE
    // ============================================================

    const busyMessage = (() => {
        if (bulkGenerating) {
            return "Generating AI descriptions...";
        }

        if (bulkPublishing) {
            return "Publishing projects to Shopify...";
        }

        if (generating) {
            return "Generating project description...";
        }

        if (publishing) {
            return "Publishing project to Shopify...";
        }

        if (checkingJob) {
            return "Checking Ascora job...";
        }

        if (loading) {
            return "Loading eligible projects...";
        }

        return "Processing...";
    })();

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <>
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    padding: 4,
                    paddingTop: 2,
                    boxSizing: "border-box",
                    overflow: "hidden",
                }}
            >
                <Typography variant="h4" gutterBottom align="center">
                    Shopify Project Publisher
                </Typography>

                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                    Review eligible closed Ascora jobs, generate project descriptions and publish approved
                    projects to Shopify.
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{
                        mb: 2,
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6">Automatic Daily Publishing</Typography>
                            <Chip
                                size="small"
                                color={autoMode?.enabled ? "success" : "default"}
                                label={autoMode?.enabled ? "ON" : "OFF"}
                            />
                            {autoMode?.isRunning && <Chip size="small" color="info" label="RUNNING" />}
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Last run: {formatAutoDate(autoMode?.lastRunUtc)} · Published:{" "}
                            {autoMode?.lastPublishedCount || 0} · Skipped: {autoMode?.lastSkippedCount || 0} ·
                            Failed: {autoMode?.lastFailedCount || 0}
                        </Typography>

                        {autoMode?.lastError && (
                            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                                {autoMode.lastError}
                            </Typography>
                        )}
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={Boolean(autoMode?.enabled)}
                                    onChange={handleAutoModeToggle}
                                    disabled={
                                        autoModeLoading ||
                                        autoModeSaving ||
                                        autoModeRunning ||
                                        autoMode?.isRunning
                                    }
                                />
                            }
                            label={autoModeSaving ? "Saving..." : "Auto Mode"}
                        />

                        <Button
                            variant="outlined"
                            startIcon={autoModeRunning ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
                            disabled={
                                autoModeLoading || autoModeSaving || autoModeRunning || autoMode?.isRunning
                            }
                            onClick={handleRunAutoModeNow}
                        >
                            {autoModeRunning ? "Running..." : "Run Now"}
                        </Button>
                    </Stack>
                </Paper>

                <Paper
                    sx={{
                        width: "100%",
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        mb: 2,
                    }}
                >
                    {/* ================================================= */}
                    {/* TOOLBAR */}
                    {/* ================================================= */}

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flex: 1,
                                minWidth: 300,
                            }}
                        >
                            <TextField
                                variant="outlined"
                                placeholder="Search eligible projects..."
                                size="small"
                                fullWidth
                                disabled={isBusy}
                                value={filterText}
                                onChange={(event) => setFilterText(event.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),

                                    endAdornment: filterText ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                disabled={isBusy}
                                                onClick={() => setFilterText("")}
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    startIcon={
                                        bulkGenerating ? <CircularProgress size={18} /> : <AutoAwesomeIcon />
                                    }
                                    disabled={isBusy || projects.length === 0}
                                    onClick={handleBulkGenerate}
                                >
                                    {bulkGenerating ? "Generating..." : "Bulk Generate AI"}
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={
                                        bulkPublishing ? <CircularProgress size={18} /> : <PublishIcon />
                                    }
                                    disabled={isBusy || publishableCount === 0}
                                    onClick={handleBulkPublish}
                                >
                                    {bulkPublishing ? "Publishing..." : `Bulk Publish (${publishableCount})`}
                                </Button>
                            </Box>

                            <Tooltip title="Refresh eligible projects">
                                <span>
                                    <IconButton onClick={fetchEligibleProjects} disabled={isBusy}>
                                        <RefreshIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                        {/* ============================================= */}
                        {/* MANUAL JOB SEARCH */}
                        {/* ============================================= */}

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 330,
                            }}
                        >
                            <TextField
                                size="small"
                                label="Ascora Job Number"
                                placeholder="e.g. J2684"
                                disabled={isBusy}
                                value={manualJobNumber}
                                onChange={(event) => setManualJobNumber(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" && !isBusy) {
                                        handleManualCheck();
                                    }
                                }}
                            />

                            <Button
                                variant="outlined"
                                startIcon={checkingJob ? <CircularProgress size={18} /> : <SearchIcon />}
                                disabled={isBusy || !manualJobNumber.trim()}
                                onClick={handleManualCheck}
                            >
                                Check Job
                            </Button>
                        </Box>
                    </Box>

                    <Divider />

                    {/* ================================================= */}
                    {/* TABLE */}
                    {/* ================================================= */}

                    {loading ? (
                        <Box sx={{ flex: 1 }} />
                    ) : error ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flex: 1,
                                p: 3,
                            }}
                        >
                            <Alert
                                severity="error"
                                sx={{
                                    maxWidth: 700,
                                }}
                            >
                                {error}
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <TableContainer
                                sx={{
                                    flex: 1,
                                    overflow: "auto",
                                }}
                            >
                                <Table stickyHeader aria-label="eligible ascora projects">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    disabled={isBusy}
                                                    active={orderBy === "jobNumber"}
                                                    direction={orderBy === "jobNumber" ? order : "asc"}
                                                    onClick={() => handleRequestSort("jobNumber")}
                                                >
                                                    <strong>Job Number</strong>
                                                </TableSortLabel>
                                            </TableCell>

                                            <TableCell>
                                                <TableSortLabel
                                                    disabled={isBusy}
                                                    active={orderBy === "jobName"}
                                                    direction={orderBy === "jobName" ? order : "asc"}
                                                    onClick={() => handleRequestSort("jobName")}
                                                >
                                                    <strong>Job Name</strong>
                                                </TableSortLabel>
                                            </TableCell>

                                            <TableCell>
                                                <TableSortLabel
                                                    disabled={isBusy}
                                                    active={orderBy === "product"}
                                                    direction={orderBy === "product" ? order : "asc"}
                                                    onClick={() => handleRequestSort("product")}
                                                >
                                                    <strong>Product</strong>
                                                </TableSortLabel>
                                            </TableCell>

                                            <TableCell>
                                                <TableSortLabel
                                                    disabled={isBusy}
                                                    active={orderBy === "suburb"}
                                                    direction={orderBy === "suburb" ? order : "asc"}
                                                    onClick={() => handleRequestSort("suburb")}
                                                >
                                                    <strong>Site</strong>
                                                </TableSortLabel>
                                            </TableCell>

                                            <TableCell>
                                                <TableSortLabel
                                                    disabled={isBusy}
                                                    active={orderBy === "completedDate"}
                                                    direction={orderBy === "completedDate" ? order : "desc"}
                                                    onClick={() => handleRequestSort("completedDate")}
                                                >
                                                    <strong>Completed</strong>
                                                </TableSortLabel>
                                            </TableCell>

                                            <TableCell>
                                                <strong>Photos</strong>
                                            </TableCell>

                                            <TableCell align="center">
                                                <strong>Status</strong>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {displayProjects.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    <Box
                                                        sx={{
                                                            py: 5,
                                                        }}
                                                    >
                                                        <Typography variant="body1" color="text.secondary">
                                                            {filterText
                                                                ? "No matching eligible projects found."
                                                                : "There are currently no eligible Ascora projects waiting to be published."}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            displayProjects.map((project) => (
                                                <TableRow
                                                    key={project.jobId || project.jobNumber}
                                                    hover={!isBusy}
                                                    onClick={
                                                        isBusy ? undefined : () => handleOpenProject(project)
                                                    }
                                                    sx={{
                                                        cursor: isBusy ? "default" : "pointer",
                                                    }}
                                                >
                                                    <TableCell>
                                                        <strong>{project.jobNumber}</strong>
                                                    </TableCell>

                                                    <TableCell>{project.jobName}</TableCell>

                                                    <TableCell>{project.product}</TableCell>

                                                    <TableCell>{project.suburb}</TableCell>

                                                    <TableCell>{formatDate(project.completedDate)}</TableCell>

                                                    <TableCell>
                                                        <Tooltip title={project.oneDriveFolderName || ""}>
                                                            <Chip
                                                                icon={<ImageIcon />}
                                                                label="Folder Ready"
                                                                size="small"
                                                                color="success"
                                                                variant="outlined"
                                                            />
                                                        </Tooltip>
                                                    </TableCell>

                                                    <TableCell align="center">
                                                        {projectDescriptions[project.jobNumber]?.trim() ? (
                                                            <Chip
                                                                icon={<CheckCircleIcon />}
                                                                label="Ready to Publish"
                                                                size="small"
                                                                color="success"
                                                            />
                                                        ) : (
                                                            <Chip
                                                                icon={<AutoAwesomeIcon />}
                                                                label="Needs AI Description"
                                                                size="small"
                                                                color="warning"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box
                                sx={{
                                    p: 2,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {filterText
                                        ? `Showing ${displayProjects.length} of ${projects.length} eligible projects`
                                        : `${projects.length} eligible project${
                                              projects.length === 1 ? "" : "s"
                                          } waiting for review`}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Paper>

                {/* ===================================================== */}
                {/* NAVIGATION */}
                {/* ===================================================== */}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 2,

                        pointerEvents: isBusy ? "none" : "auto",

                        opacity: isBusy ? 0.5 : 1,
                    }}
                >
                    <BackToCPQButton />

                    <PreviousPageButtonCPQ />
                </Box>

                {/* ===================================================== */}
                {/* PROJECT REVIEW DIALOG */}
                {/* ===================================================== */}

                <Dialog
                    open={openDialog}
                    onClose={isBusy ? undefined : handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        {selectedProject
                            ? `Review Project - ${selectedProject.jobNumber}`
                            : "Project Eligibility"}
                    </DialogTitle>

                    <Divider />

                    <DialogContent>
                        {!selectedProject ? (
                            <Box
                                sx={{
                                    py: 3,
                                }}
                            >
                                <Alert severity="warning" icon={<ErrorOutlineIcon />}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Not Eligible
                                    </Typography>

                                    <Typography variant="body2">
                                        {eligibilityReason ||
                                            "This Ascora job cannot currently be published."}
                                    </Typography>
                                </Alert>
                            </Box>
                        ) : (
                            <Stack
                                spacing={2.5}
                                sx={{
                                    pt: 2,
                                }}
                            >
                                <Alert severity="success" icon={<CheckCircleIcon />}>
                                    This project is eligible for publishing.
                                </Alert>

                                <TextField
                                    fullWidth
                                    disabled={isBusy}
                                    label="Job Number"
                                    value={selectedProject.jobNumber || ""}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    disabled={isBusy}
                                    label="Job Name"
                                    value={selectedProject.jobName || ""}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            md: "1fr 1fr",
                                        },
                                        gap: 2,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        disabled={isBusy}
                                        label="Product"
                                        value={selectedProject.product || ""}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        disabled={isBusy}
                                        label="Work Undertaken"
                                        value={selectedProject.workUndertaken || ""}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            md: "1fr 1fr",
                                        },
                                        gap: 2,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        disabled={isBusy}
                                        label="Site Suburb / City"
                                        value={selectedProject.suburb || ""}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        disabled={isBusy}
                                        label="Completion Date"
                                        value={formatDate(selectedProject.completedDate)}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                    />
                                </Box>

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <FolderIcon color="primary" />

                                        <Typography variant="subtitle1" fontWeight={600}>
                                            OneDrive Photos
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mb: 1,
                                        }}
                                    >
                                        Folder: <strong>{selectedProject.oneDriveFolderName}</strong>
                                    </Typography>

                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        <Chip
                                            icon={<FolderIcon />}
                                            label="Matching Folder Found"
                                            color="success"
                                            variant="outlined"
                                        />
                                    </Stack>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: "block",
                                            mt: 1.5,
                                        }}
                                    >
                                        Four random photos will be downloaded from the matching OneDrive
                                        folder during publishing. One becomes the thumbnail and the remaining
                                        three become Image 1, Image 2 and Image 3.
                                    </Typography>
                                </Paper>

                                <Divider />

                                <Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            AI Generated Description
                                        </Typography>

                                        <Button
                                            variant="outlined"
                                            startIcon={
                                                generating ? (
                                                    <CircularProgress size={18} />
                                                ) : (
                                                    <AutoAwesomeIcon />
                                                )
                                            }
                                            disabled={isBusy}
                                            onClick={handleGenerateDescription}
                                        >
                                            {description.trim() ? "Regenerate" : "Generate"}
                                        </Button>
                                    </Box>

                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={7}
                                        maxRows={14}
                                        disabled={isBusy}
                                        label="Job Description"
                                        value={description}
                                        onChange={(event) => {
                                            const value = event.target.value;

                                            setDescription(value);

                                            if (selectedProject?.jobNumber) {
                                                setProjectDescriptions((current) => ({
                                                    ...current,

                                                    [selectedProject.jobNumber]: value,
                                                }));
                                            }
                                        }}
                                        placeholder="Click Generate to create a description, then review and edit it here."
                                        helperText={`${description.length} characters — this is the only Shopify project field that can be edited here.`}
                                    />
                                </Box>

                                {!canPublish && (
                                    <Alert severity="info">
                                        Generate or enter a project description before publishing. Publish
                                        remains disabled until all required project data is available.
                                    </Alert>
                                )}
                            </Stack>
                        )}
                    </DialogContent>

                    <Divider />

                    <DialogActions
                        sx={{
                            px: 3,
                            py: 2,
                        }}
                    >
                        <Button onClick={handleCloseDialog} disabled={isBusy}>
                            Close
                        </Button>

                        {selectedProject && (
                            <Button
                                variant="contained"
                                startIcon={publishing ? <CircularProgress size={18} /> : <PublishIcon />}
                                disabled={isBusy || !canPublish}
                                onClick={handlePublish}
                            >
                                {publishing ? "Publishing..." : "Publish to Shopify"}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

                {/* ===================================================== */}
                {/* SNACKBAR */}
                {/* ===================================================== */}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{
                            width: "100%",
                        }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>

            {/* ========================================================= */}
            {/* GLOBAL LOADING LOCK */}
            {/* Blocks ALL clicks / keyboard interaction while busy */}
            {/* ========================================================= */}

            <Backdrop
                open={isBusy}
                sx={(theme) => ({
                    zIndex: theme.zIndex.modal + 100,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                })}
            >
                <CircularProgress size={52} color="inherit" />

                <Typography variant="h6" color="inherit">
                    {busyMessage}
                </Typography>
            </Backdrop>
        </>
    );
}

export default AscoraProjectsPublisher;
